import {Component, EventEmitter, Input, Output} from '@angular/core';
import {AdDatepickerComponent} from './ad-datepicker/ad-datepicker.component';

import {FormsModule} from '@angular/forms';
import {ConnectionPositionPair, OverlayModule} from '@angular/cdk/overlay';

export type DateRestriction = 'none' | 'past-only' | 'future-only';

@Component({
  selector: 'app-ad-date-picker-wrapper',
  templateUrl: './ad-date-picker-wrapper.component.html',
  imports: [AdDatepickerComponent, FormsModule, OverlayModule],
  standalone: true,
  styleUrls: ['./ad-date-picker-wrapper.component.scss']
})
export class AdDatePickerWrapperComponent {

  private _date: string | null = null;

  overlayPositions: ConnectionPositionPair[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 6
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 6
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -6
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -6
    },
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 6
    },
    {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -6
    }
  ];

  @Input() disabled: boolean = false;
  @Input() position: string = '';
  @Input() dateRestriction: DateRestriction = 'none';  // NEW
  @Input() minDate?: Date;  // NEW
  @Input() maxDate?: Date;  // NEW
  @Input() enableTime: boolean = false;

  timeSelectionMode = false;
  selectedTime = '00:00';
  tempSelectedDateString: string | null = null;

  @Input()
  set date(value: string | null) {
    this._date = value;
    if (this._date) {
      let datePart = this._date;
      if (this._date.includes('T')) {
        datePart = this._date.split('T')[0];
        this.selectedTime = this._date.split('T')[1].substring(0, 5);
      } else if (this._date.includes(' ')) {
        datePart = this._date.split(' ')[0];
        this.selectedTime = this._date.split(' ')[1].substring(0, 5);
      } else {
        this.selectedTime = '00:00';
      }
      const [year, month, day] = datePart.split('-').map(Number);
      this.selectedDate = new Date(year, month - 1, day);
    } else {
      this.selectedDate = null;
    }
  }

  @Input() placeholder: string = 'Select Date';
  @Output() dateSelected = new EventEmitter<string>();

  showCalendar = false;
  selectedDate: Date | null = null;

  get displayValue(): string {
    if (!this.selectedDate) return '';
    const year = this.selectedDate.getFullYear();
    const month = (this.selectedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = this.selectedDate.getDate().toString().padStart(2, '0');
    let str = `${month}/${day}/${year}`;
    if (this.enableTime) {
      str += ` ${this.selectedTime}`;
    }
    return str;
  }

  // NEW: Calculate effective min/max dates based on restriction
  get effectiveMinDate(): Date | undefined {
    if (this.minDate) return this.minDate;
    if (this.dateRestriction === 'future-only') {
      // Future only: minimum is today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    }
    return undefined;
  }

  get effectiveMaxDate(): Date | undefined {
    if (this.maxDate) return this.maxDate;
    if (this.dateRestriction === 'past-only') {
      // Past only: maximum is today
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      return today;
    }
    return undefined;
  }

  onDateSelected(date: Date) {
    this.selectedDate = date;

    if (date) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      if (this.enableTime) {
        this.tempSelectedDateString = formattedDate;
        this.timeSelectionMode = true;
      } else {
        this.showCalendar = false;
        this.dateSelected.emit(formattedDate);
      }
    } else {
      this.showCalendar = false;
      this.dateSelected.emit('');
    }
  }

  confirmTimeSelection() {
    if (this.tempSelectedDateString) {
      this.dateSelected.emit(this.tempSelectedDateString + 'T' + this.selectedTime + ':00');
    }
    this.timeSelectionMode = false;
    this.showCalendar = false;
  }

  openCalendar(event: MouseEvent) {
    if (this.disabled) return;
    this.timeSelectionMode = false;
    this.tempSelectedDateString = null;
    event.stopPropagation();
    this.showCalendar = true;
  }
}

